import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditFaqModal from 'components/admin/common/modal/EditFaqModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';

const Faq = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createFaqData,
        postError: creatingError,
        postLoading: creating,
        putData: updateFaqData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteFaqData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.FAQ);

    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteFaq = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditFaq = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteFaq, modalData: faqToDelete, openModal: openDeleteFaq, closeModal: closedDeleteFaq } = useModalState({
        onClose: handleCloseDeleteFaq
    });

    const { isOpen: isOpenEditFaq, modalData: selectedFaq, openModal: openEditFaq, closeModal: closedEditFaq } = useModalState({
        onClose: handleCloseEditFaq
    });

    const validationRules = [
        { field: 'question', method: 'required' },
        { field: 'answer', method: 'required' }
    ];

    const handleModalSave = useCallback(async (updatedFaq) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedFaq, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        try {
            if (updatedFaq.id) {
                await updateFaqData(updatedFaq.id, updatedFaq, true);
                setSuccessMessage("FAQ actualizado con éxito.");
            } else {
                await createFaqData(updatedFaq, true);
                setSuccessMessage("FAQ creado con éxito.");
            }
            closedEditFaq();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateFaqData, createFaqData, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newFaq = {
            question: '',
            answer: ''
        };
        openEditFaq(newFaq);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (faqToDelete) {
            try {
                await deleteFaqData(faqToDelete.id);
                setSuccessMessage("FAQ eliminado con éxito.");
                refetch();
                closedDeleteFaq();
            } catch (error) {
                console.error(error);
            }
        }
    }, [faqToDelete, deleteFaqData, refetch]);

    const columns = useMemo(() => [
        { field: 'question', header: 'Pregunta', sortable: true },
        { field: 'answer', header: 'Respuesta', sortable: true }
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">FAQ</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar FAQ
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditFaq}
                onDelete={openDeleteFaq}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="question"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditFaqModal
                show={isOpenEditFaq}
                faq={selectedFaq}
                handleClose={closedEditFaq}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
                requestError={creatingError || updatingError}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteFaq}
                onHide={closedDeleteFaq}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(Faq);